import { useState, useMemo } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogPost, getPostsForRegion } from "@/lib/blog-data";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search, X, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "@/lib/locations";
import { REGIONS } from "@/lib/locations";


const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const location = useLocation();

  const isSacramento = location.regionSlug === "sacramento-area";

  const allPosts = useMemo(() => {
    return getPostsForRegion(location.name, location.regionSlug);
  }, [location.name, location.regionSlug]);

  const categories = useMemo(() => {
    return ["All", ...Array.from(new Set(allPosts.map(post => post.category)))];
  }, [allPosts]);

  const filteredPosts = useMemo(() => {
    return allPosts.filter((post) => {
      const searchStr = searchQuery.toLowerCase();
      const matchesSearch =
        post.title.toLowerCase().includes(searchStr) ||
        post.excerpt.toLowerCase().includes(searchStr) ||
        post.category.toLowerCase().includes(searchStr);
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, allPosts]);

  return (
    <div className="flex min-h-screen flex-col">
      <SEO
        title={`Cleaning Tips & Local Guides for ${location.name} — Uplevel Cleaning Blog`}
        description={`Expert advice on house cleaning, organization, and home maintenance in ${location.name}. Read our latest tips to keep your home spotless.`}
        keywords={`${location.name.toLowerCase()} cleaning blog, house cleaning tips, home organization ${location.name.toLowerCase()}, move out cleaning guide, uplevel cleaning`}
      />
      <Navbar />
      <main className="flex-1">
        <section className="py-20 bg-muted/30">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center mb-16">
              {/* Region badge */}
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <MapPin className="h-4 w-4" />
                {location.name}
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">Uplevel Insights</h1>
              <p className="text-xl text-muted-foreground mb-10">
                Expert tips, local guides, and cleaning secrets for the modern {location.name} homeowner.
              </p>

              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles by title, topic, or keyword..."
                  className="pl-12 h-14 text-lg rounded-2xl border-none shadow-lg focus-visible:ring-primary"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap justify-center gap-2 mt-8">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium transition-colors border",
                      selectedCategory === category
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:bg-muted"
                    )}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Card key={post.slug} className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all group flex flex-col">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-primary text-primary-foreground text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <CardHeader className="flex-1">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {post.date}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </div>
                      </div>
                      <CardTitle className="text-xl leading-tight group-hover:text-primary transition-colors">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </CardTitle>
                      <p className="text-muted-foreground text-sm mt-3 line-clamp-3">
                        {post.excerpt}
                      </p>
                    </CardHeader>
                    <CardFooter className="pt-0">
                      <Button asChild variant="link" className="p-0 h-auto text-primary font-bold group/btn">
                        <Link to={`/blog/${post.slug}`} className="flex items-center gap-2">
                          Read Full Article
                          <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="bg-muted w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-2xl font-bold mb-2">No articles found</h3>
                <p className="text-muted-foreground">
                  We couldn't find any articles matching "{searchQuery}". Try a different keyword.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => { setSearchQuery(""); setSelectedCategory("All"); }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
